import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const GlobalCollaborationPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seo = {
    metaTitle: "Global Collaboration | Custom Software & AI Development",
    metaDescription: "Meet our founders in India and our regional Business Development Manager Nancy Karimi in Kenya. Discover how we collaborate across borders to deliver premium custom software and automation solutions.",
    keywords: "global collaboration, software development company, custom software Kenya, India software company, Nancy Karimi, Anbu Liyon, Giridharan Sivaramakrishnan, Giridharansivaramakrishnan, bytepairs giridharan, business automation, CRM & ERP solutions",
  };

  const steps = [
    { num: "01", title: "Client Consultation", desc: " Nancy Karimi scopes your operational goals and business objectives locally in Kenya." },
    { num: "02", title: "Business Analysis", desc: "The regional team collaborates with technical leads in India to document workflow bottlenecks." },
    { num: "03", title: "Solution Planning", desc: "Giridharan creates custom database architectures, API mappings, and systems specifications." },
    { num: "04", title: "UI/UX Design", desc: "Interactive Figma wireframes are crafted to let you preview and approve the user experience." },
    { num: "05", title: "Custom Development", desc: "Core software is built in India with clean, optimized code—free of legacy subscription bloat." },
    { num: "06", title: "Testing & QA", desc: "Rigorous quality checks ensure the application loads in milliseconds and runs without error." },
    { num: "07", title: "Deployment", desc: "Deploying to secure cloud networks (AWS/Vercel) with automated backup routines." },
    { num: "08", title: "Uptime Support", desc: "Nancy manages local relations while Indian dev leads handle server maintenance and tweaks." },
  ];

  return (
    <>
      <Helmet>
        <title>{seo.metaTitle}</title>
        <meta name="description" content={seo.metaDescription} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href="https://bytepairs.com/global-collaboration" />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content={seo.metaTitle} />
        <meta property="og:description" content={seo.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bytepairs.com/global-collaboration" />
        <meta property="og:image" content="https://bytepairs.com/assets/giri.jpg" />
        <meta property="og:image" content="https://bytepairs.com/assets/anbu.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.metaTitle} />
        <meta name="twitter:description" content={seo.metaDescription} />
        <meta name="twitter:image" content="https://bytepairs.com/assets/nancy.jpg" />

        {/* WebPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": seo.metaTitle,
            "description": seo.metaDescription,
            "url": "https://bytepairs.com/global-collaboration"
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://bytepairs.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Global Collaboration",
                "item": "https://bytepairs.com/global-collaboration"
              }
            ]
          })}
        </script>

        {/* Organization / Collaborator Spotlight Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "mainEntity": {
              "@type": "Organization",
              "name": "Byte Pairs",
              "url": "https://bytepairs.com/",
              "logo": "https://bytepairs.com/assets/bytepairs-logo.png",
              "founder": [
                {
                  "@type": "Person",
                  "name": "Anbu Liyon"
                },
                {
                  "@type": "Person",
                  "name": "Giridharan Sivaramakrishnan"
                }
              ],
              "employee": {
                "@type": "Person",
                "name": "Nancy Karimi",
                "jobTitle": "Business Development Manager – Kenya",
                "image": "https://bytepairs.com/assets/nancy.jpg",
                "address": {
                  "@type": "PostalAddress",
                  "addressCountry": "KE"
                }
              }
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="pt-20">
          {/* 1. Hero Section */}
          <section className="relative py-24 bg-gradient-to-br from-primary/5 via-muted/30 to-accent/5 overflow-hidden border-b border-border">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(rgba(234,179,8,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(234,179,8,0.1)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
            </div>
            <div className="max-w-6xl mx-auto px-6 relative z-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Icon name="Globe" size={16} />
                  <span>One Team, Global Synergy</span>
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-4xl md:text-6xl font-black mb-6 tracking-tight"
              >
                Global <span className="text-gradient">Collaboration</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed mb-8"
              >
                Great digital solutions are built by bringing together diverse, regional expertise. By combining localized market understanding with custom-engineered software development in India, we deliver high-performance tools that help businesses grow worldwide.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="flex justify-center gap-4"
              >
                <Button
                  variant="default"
                  iconName="Phone"
                  iconPosition="left"
                  className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand font-bold"
                  onClick={() => navigate('/contact-engagement-hub')}
                >
                  Schedule A Call
                </Button>
              </motion.div>
            </div>
          </section>

          {/* 2. Why We Believe in Global Collaboration */}
          <section className="py-20 bg-background">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Global Collaboration Works</h2>
                <p className="text-text-secondary max-w-2xl mx-auto text-base">
                  Distributing our reach enables us to design software that aligns with your local environment while utilizing world-class development frameworks.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="card p-8 hover:-translate-y-1 transition-transform duration-300">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-6">
                    <Icon name="Target" size={24} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Local Market Scoping</h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    We scope requirements directly in your operating region, ensuring we design custom tools that fit local laws, currencies, and client demographics.
                  </p>
                </div>

                <div className="card p-8 hover:-translate-y-1 transition-transform duration-300">
                  <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center text-success mb-6">
                    <Icon name="Cpu" size={24} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">High-Performance Dev</h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    By developing our core logic in India, we bring top-tier database design, software engineering, and API integration speed directly to your business.
                  </p>
                </div>

                <div className="card p-8 hover:-translate-y-1 transition-transform duration-300">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent mb-6">
                    <Icon name="Clock" size={24} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">Seamless Communication</h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    With local regional coordinators on the ground, your messages are answered within hours, bridging time zones with friendly developer accountability.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 3. Leadership & 4. Regional Collaboration */}
          <section className="py-20 bg-muted/20 border-t border-b border-border/60">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Core Team & Regional Representative</h2>
                <p className="text-text-secondary max-w-2xl mx-auto">
                  Meet the leaders coordinating our technology builds and regional collaborations.
                </p>
              </div>

              {/* Core Founders */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16 max-w-4xl mx-auto">
                <div className="card p-8 flex flex-col items-center text-center relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/10 to-transparent rounded-bl-[80px] -z-10"></div>
                  <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-primary/20 mb-6">
                    <img
                      src="/assets/anbu.png"
                      alt="Anbu Liyon"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center 20%' }}
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-1">Anbu Liyon</h3>
                  <span className="text-primary font-semibold text-sm mb-4">Founder & Lead Strategy</span>
                  <p className="text-text-secondary text-xs leading-relaxed max-w-xs mb-6">
                    Responsible for vision, leadership, and company direction. Anbu coordinates client solutions from India, checking that every custom build aligns with the client's business strategy.
                  </p>
                  <div className="flex items-center space-x-3">
                    <a
                      href="https://in.linkedin.com/in/anbu-liyon-068352284"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-primary/10 hover:bg-primary/25 text-primary transition-all duration-300 flex items-center justify-center"
                      title="LinkedIn Profile"
                    >
                      <Icon name="Linkedin" size={16} />
                    </a>
                    <a
                      href="https://anbuliyon.bytepairs.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-primary/10 hover:bg-primary/25 text-primary transition-all duration-300 flex items-center justify-center"
                      title="Portfolio Website"
                    >
                      <Icon name="Globe" size={16} />
                    </a>
                    <button
                      className="p-2 rounded-lg bg-primary/10 hover:bg-primary/25 text-primary transition-all duration-300 flex items-center justify-center"
                      title="Share Profile"
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: 'Anbu Liyon - Portfolio',
                            text: "Check out Anbu Liyon's professional portfolio!",
                            url: 'https://anbuliyon.bytepairs.com/'
                          }).catch(err => console.log(err));
                        } else {
                          navigator.clipboard.writeText('https://anbuliyon.bytepairs.com/');
                          alert("Portfolio link copied to clipboard!");
                        }
                      }}
                    >
                      <Icon name="Share2" size={16} />
                    </button>
                  </div>
                </div>

                <div className="card p-8 flex flex-col items-center text-center relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-success/10 to-transparent rounded-bl-[80px] -z-10"></div>
                  <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-success/20 mb-6">
                    <img
                      src="/assets/giri.jpg"
                      alt="Giridharan Sivaramakrishnan"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-1">Giridharan Sivaramakrishnan</h3>
                  <span className="text-success font-semibold text-sm mb-4">Co-Founder & Technology Lead</span>
                  <p className="text-text-secondary text-xs leading-relaxed max-w-xs mb-6">
                    Responsible for technological direction, solution architecture, and coding. Giri leads product engineering in India, ensuring database optimization, mobile responsiveness, and speed.
                  </p>
                  <div className="flex items-center space-x-3">
                    <a
                      href="https://www.linkedin.com/in/giridharan-sivaramakrishnan-6325ab293/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-success/10 hover:bg-success/25 text-success transition-all duration-300 flex items-center justify-center"
                      title="LinkedIn Profile"
                    >
                      <Icon name="Linkedin" size={16} />
                    </a>
                    <a
                      href="https://giridharan.bytepairs.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-success/10 hover:bg-success/25 text-success transition-all duration-300 flex items-center justify-center"
                      title="Portfolio Website"
                    >
                      <Icon name="Globe" size={16} />
                    </a>
                    <button
                      className="p-2 rounded-lg bg-success/10 hover:bg-success/25 text-success transition-all duration-300 flex items-center justify-center"
                      title="Share Profile"
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: 'Giridharan Sivaramakrishnan - Portfolio',
                            text: "Check out Giridharan Sivaramakrishnan's professional portfolio!",
                            url: 'https://giridharan.bytepairs.com/'
                          }).catch(err => console.log(err));
                        } else {
                          navigator.clipboard.writeText('https://giridharan.bytepairs.com/');
                          alert("Portfolio link copied to clipboard!");
                        }
                      }}
                    >
                      <Icon name="Share2" size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Nancy Karimi Featured Spotlight (Regional Representative) */}
              <div className="card max-w-4xl mx-auto p-8 md:p-12 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-accent/15 via-transparent to-transparent rounded-bl-[120px] -z-10"></div>
                <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                  <div className="relative flex-shrink-0">
                    <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-accent/30 relative">
                      <img
                        src="/assets/nancy.jpg"
                        alt="Nancy Karimi"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-accent rounded-full flex items-center justify-center border-2 border-background shadow-lg">
                      <Icon name="Globe" size={18} color="white" />
                    </div>
                  </div>

                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-3 py-1 rounded-full text-xs font-semibold mb-3">
                      <Icon name="Handshake" size={12} />
                      <span>Regional Outreach Representative</span>
                    </div>
                    <h3 className="text-3xl font-black mb-1">Nancy Karimi</h3>
                    <span className="text-accent font-bold text-sm block mb-4">Business Development Manager – Kenya</span>
                    <p className="text-text-secondary text-sm leading-relaxed mb-6">
                      Based in Kenya, Nancy is our vital regional collaborator. She leads business development, client engagement, and sales coordination, ensuring Kenyan enterprises can seamlessly scope and implement custom digital transformations. Working closely with our technical founders, Nancy translates local requirements into detailed product scoping blueprints.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        variant="outline"
                        iconName="Mail"
                        iconPosition="left"
                        className="border-accent/40 text-accent hover:bg-accent/5 hover:border-accent"
                        onClick={() => navigate('/contact-engagement-hub')}
                      >
                        Collaborate with Nancy
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 5. How We Work Together */}
          <section className="py-20 bg-background">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">How Our Global Teams Collaborate</h2>
                <p className="text-text-secondary max-w-2xl mx-auto text-base">
                  We use a structured digital project lifecycle to sync local scoping with core engineering, delivering transparency at every stage.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {steps.map((step, idx) => (
                  <div key={idx} className="card p-6 relative overflow-hidden group">
                    <div className="absolute top-2 right-4 text-4xl font-black text-muted-foreground/10 group-hover:text-primary/10 transition-colors duration-300">
                      {step.num}
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-foreground group-hover:text-primary transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-text-secondary text-xs leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 6. Why This Benefits Our Clients & 7. Our Commitment */}
          <section className="py-20 bg-muted/10 border-t border-border/60">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">The Client Advantage</h2>
                <p className="text-text-secondary max-w-2xl mx-auto">
                  By working with an internationally collaborating startup, you get localized communication alongside high-grade technical execution.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <div className="flex space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <Icon name="Check" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Local Project Ownership</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      Nancy Karimi provides local meetings, scoping alignment, and support in Kenya, eliminating timezone delays.
                    </p>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <Icon name="Check" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Honest Pricing Model</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      Enjoy a one-time build cost with zero licensing overhead. We build what you need, and you own the code forever.
                    </p>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <Icon name="Check" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Direct Developer Access</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      No bloated project management tiers. You coordinate requirements directly with Nancy and the technical developers.
                    </p>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <Icon name="Check" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Scalable Cloud Architecture</h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      Engineered with modern stacks (React, Next.js, APIs) for high speed, offline accessibility, and uptime safety.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 8. Closing Section */}
          <section className="py-24 bg-gradient-to-r from-primary/15 via-accent/5 to-primary/15 text-center border-t border-border">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tight">
                Let's Build Something Amazing Together
              </h2>
              <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto leading-relaxed">
                Whether you are based in Kenya, India, or anywhere else in the world, Byte Pairs is ready to deliver tailored business automations, custom ERP software, and high-converting marketing sites.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  variant="default"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand font-bold"
                  onClick={() => navigate('/contact-engagement-hub')}
                >
                  Start Your Project
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Calculator"
                  iconPosition="left"
                  className="border-primary/20 text-primary hover:bg-primary/5"
                  onClick={() => navigate('/contact-engagement-hub?tab=estimator')}
                >
                  Calculate Estimate
                </Button>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default GlobalCollaborationPage;
